import { useMemo } from 'react';
import DataGridV2, {
  type DataGridColumn
} from '@components/atoms/datatable/data-grid-v2';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import cn from 'classnames';
import PageHeader from '@components/layout/page-header.tsx';
import Loader from '@components/atoms/loaders/loader.tsx';
import { StatusBadge } from '@components/atoms/ui-kit/badge';
import { useGetCustomerBoMandates } from '@utils/api/back-office/customer-bo/use-get-customer-bo-mandates.tsx';
import type { StatusTone } from '@utils/constants/status-map';

type Row = {
  id: number;
  mollie_id: string;
  is_primary: string;
  status: string;
  method: string;
  details: string;
};

const getMandateStatusTone = (status: string): StatusTone => {
  switch (status.toLowerCase()) {
    case 'valid':
      return 'success';
    case 'invalid':
      return 'danger';
    case 'pending':
      return 'info';
    default:
      return 'neutral';
  }
};

export const CustomerBoMandates = () => {
  const { t } = useTranslation();
  const { customerId } = useParams<{ customerId?: string }>();
  const numericId = Number(customerId) || 0;

  const { data: mandates, isLoading } = useGetCustomerBoMandates(numericId);

  const rows = useMemo<Row[]>(() => {
    const result = mandates?.result ?? [];
    return result.map(mandate => ({
      id: mandate.id,
      mollie_id: mandate.mollie_id,
      is_primary: mandate.is_primary ? t('yes') : t('no'),
      status: mandate.status,
      method: mandate.method,
      details:
        typeof mandate.details === 'object'
          ? Object.entries(mandate.details)
              .map(([k, v]) => `${k}: ${v}`)
              .join(', ')
          : String(mandate.details ?? '')
    }));
  }, [mandates, t]);

  const columns = useMemo<DataGridColumn<Row>[]>(() => {
    return [
      {
        header: t('id'),
        field: 'id',
        maxWidth: 90,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'number'
      },
      {
        header: t('mollie-id'),
        field: 'mollie_id',
        minWidth: 220,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        enableRowCopyOnClick: true
      },
      {
        header: t('is-primary'),
        field: 'is_primary',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'select',
        filterOptions: [
          { label: t('all'), value: '' },
          { label: t('yes'), value: t('yes') },
          { label: t('no'), value: t('no') }
        ],
        cell: ({ getValue }) => {
          const isPrimary = getValue();
          const tone: StatusTone =
            isPrimary === t('yes') ? 'success' : 'neutral';
          return <StatusBadge status={String(isPrimary)} tone={tone} />;
        }
      },
      {
        header: t('status'),
        field: 'status',
        maxWidth: 160,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text',
        cell: ({ getValue }) => {
          const status = String(getValue() ?? '');
          return (
            <StatusBadge status={status} tone={getMandateStatusTone(status)} />
          );
        }
      },
      {
        header: t('method'),
        field: 'method',
        maxWidth: 140,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      },
      {
        header: t('details'),
        field: 'details',
        minWidth: 280,
        enableSorting: false,
        enableFiltering: false,
        filterVariant: 'text'
      }
    ];
  }, [t]);

  return (
    <div className={cn('py-2 w-full')}>
      {isLoading ? (
        <div className="h-screen w-full flex justify-center items-center -mt-24">
          <Loader height={10} width={3} />
        </div>
      ) : (
        <div className="space-y-4">
          <PageHeader
            title={t('customer-mandates')}
            contentClassName={'py-0 pb-4'}
            caption={t(
              'list-of-all-the-mandates-including-their-name-contact-and-actions'
            )}
          />

          <DataGridV2<Row>
            data={rows}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination
            totalItems={mandates?.total_results ?? rows.length}
            pageSize={25}
            pageSizeOptions={[25, 50, 75, 100]}
            enableSorting
            manualSorting
            enableFiltering
            manualFiltering
            showColumnFilters={false}
            filterMode="popover"
            height="75vh"
            dense
            striped
            grid={false}
            loading={isLoading}
            getRowId={row => String(row.id)}
          />
        </div>
      )}
    </div>
  );
};
